<template>
  <Dialog
    :visible="visible"
    modal
    :style="dialogStyle"
    class="soha-file-preview-dialog"
    :content-style="contentStyle"
    dismissable-mask
    @update:visible="updateVisible"
  >
    <template #header>
      <div class="d-flex justify-content-between align-items-center w-100 pl-4" style="gap: 1rem">
        <span class="font-weight-bolder font-size-h5 text-dark">{{ titleText }}</span>
        <soha-button
          :text="downloadLabel"
          icon="download"
          outline
          variant="mehr"
          height="30"
          width="140"
          @btn-click="downloadFile"
        />
      </div>
    </template>

    <div class="preview-content">
      <div v-if="isLoading" class="preview-loading-overlay">
        <soha-page-loading />
      </div>

      <!-- PDF Section -->
      <div v-if="isPdf" class="preview-iframe-wrapper">
        <iframe title="PDF Preview" frameborder="0" class="preview-iframe" :src="pdfViewerUrl" @load="onContentLoad" />
      </div>

      <!-- Image Section -->
      <div v-else-if="isImage" class="preview-image-wrapper">
        <viewer
          :options="viewerOptions"
          class="viewer-inline-container"
          @inited="onViewerInited"
        >
          <img :src="fileUrl" class="d-none" @load="onContentLoad" @error="onContentError">
        </viewer>

        <!-- Custom Dark Control Bar -->
        <div class="soha-viewer-controls">
          <div class="controls-dark-box">
            <!-- Rotate Left -->
            <button type="button" class="control-btn" @click="viewerInstance?.rotate(-90)" title="چرخش به چپ">
              <i class="pi pi-refresh rotate-left-icon"></i>
            </button>

            <div class="divider-v"></div>

            <!-- Zoom Out (-) -->
            <button type="button" class="control-btn" @click="stepZoom(-0.2)" title="کوچک‌نمایی">
              <i class="pi pi-minus"></i>
            </button>

            <!-- Zoom Slider -->
            <input 
              type="range" min="0.1" max="3" step="0.01" 
              v-model.number="zoomLevel" 
              @input="handleSliderChange"
              class="zoom-range-input"
            >

            <!-- Zoom In (+) -->
            <button type="button" class="control-btn" @click="stepZoom(0.2)" title="بزرگ‌نمایی">
              <i class="pi pi-plus"></i>
            </button>

            <div class="divider-v"></div>

            <!-- One to One -->
            <button type="button" class="control-btn" @click="viewerInstance?.toggle()" title="اندازه واقعی">
              <span class="one-to-one-text">1:1</span>
            </button>

            <!-- Rotate Right -->
            <div class="divider-v"></div>
            <button type="button" class="control-btn" @click="viewerInstance?.rotate(90)" title="چرخش به راست">
              <i class="pi pi-refresh"></i>
            </button>
          </div>
          <!-- Zoom Badge -->
          <div class="zoom-badge">{{ Math.round(zoomLevel * 100) }}%</div>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import 'viewerjs/dist/viewer.css';
import { component as Viewer } from 'v-viewer';

const props = defineProps({
  visible: Boolean,
  fileUrl: String,
  title: String,
  downloadLabel: { type: String, default: 'دانلود فایل' },
  pdfViewerBaseUrl: { type: String, default: '/pdf-viewer/web/index.html?file=' },
});

const emit = defineEmits(['update:visible']);
const isLoading = ref(false);
const viewerInstance = ref(null);
const zoomLevel = ref(1);

const viewerOptions = {
  inline: true,
  button: false,
  navbar: false,
  title: false,
  toolbar: false,
  tooltip: false,
  zoom(e) {
    // Sync slider when user zooms using mouse wheel
    zoomLevel.value = e.detail.ratio;
  }
};

const isPdf = computed(() => /\.pdf(\?.*)?$/i.test(props.fileUrl || ''));
const isImage = computed(() => /\.(png|jpe?g|jpg|gif|svg|webp)(\?.*)?$/i.test(props.fileUrl || ''));
const titleText = computed(() => props.title || (isPdf.value ? 'پیش‌نمایش PDF' : 'پیش‌نمایش تصویر'));
const pdfViewerUrl = computed(() => {
  if (!props.fileUrl) return '';
  // navpanes=0 hides the sidebar, toolbar=0 attempts to hide the top bar
  return `${props.fileUrl}#navpanes=0&toolbar=0`;
});

const dialogStyle = { width: '90vw', maxWidth: '1400px' };
const contentStyle = { padding: '0', overflow: 'hidden', backgroundColor: '#ffffff' };

const onViewerInited = (viewer) => {
  viewerInstance.value = viewer;
};

const handleSliderChange = () => {
  if (viewerInstance.value) {
    // FIX: Added .value to zoomLevel
    viewerInstance.value.zoomTo(zoomLevel.value);
  }
};

const stepZoom = (ratio) => {
  if (viewerInstance.value) {
    viewerInstance.value.zoom(ratio);
  }
};

const onContentLoad = () => { isLoading.value = false; };
const onContentError = () => { isLoading.value = false; };
const updateVisible = (value) => emit('update:visible', value);

const downloadFile = () => {
  if (props.fileUrl) window.open(props.fileUrl, '_blank');
};

watch(() => [props.visible, props.fileUrl], ([v, url]) => {
  if (v && url) {
    isLoading.value = true;
    zoomLevel.value = 1;
  }
});
</script>

<style scoped>
/* کدهای استایل مشابه پیام قبلی است */
.preview-content {
  position: relative;
  height: 80vh;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.preview-image-wrapper {
  position: relative;
  flex-grow: 1;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
}

.viewer-inline-container {
  flex-grow: 1;
  width: 100%;
}

:deep(.viewer-container) {
  background-color: transparent !important;
}

.soha-viewer-controls {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1000;
  pointer-events: none;
}

.controls-dark-box {
  pointer-events: auto;
  background-color: #2c2c2c;
  padding: 8px 16px;
  border-radius: 50px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}

.control-btn {
  background: transparent;
  border: none;
  color: #ffffff;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  border-radius: 50%;
}

.control-btn:hover {
  background-color: #444444;
}

.control-btn i {
  font-size: 1.1rem;
}

.rotate-left-icon {
  transform: scaleX(-1);
}

.one-to-one-text {
  font-weight: bold;
  font-size: 0.8rem;
}

.divider-v {
  width: 1px;
  height: 20px;
  background-color: #555555;
}

.zoom-range-input {
  width: 150px;
  cursor: pointer;
  accent-color: #ffffff;
}

.zoom-badge {
  background: rgba(44, 44, 44, 0.8);
  color: white;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 0.7rem;
  margin-top: 8px;
}

.preview-iframe-wrapper, .preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.d-none { display: none; }

.preview-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  z-index: 10;
}
</style>